import { Controller } from "@hotwired/stimulus";

/**
 * カート確認ドロワー内での商品数量操作と注文確定機能を管理するStimulusコントローラー
 *
 * 使用場所:
 * - カート確認ドロワー内の各商品行
 * - 注文確定・戻るボタン
 *
 * 主な機能:
 * - 商品数量の増減操作（+/-ボタン、直接入力）
 * - 数量制限チェックとエラー表示
 * - 数量に応じた視覚的スタイル変更（0:グレー、1以上:緑系）
 * - 日別・全体の価格・税額計算とリアルタイム更新
 * - ドロワーの開閉制御
 * - 注文データの収集とフォーム送信
 *
 * cart_controllerとの違い:
 * - cart_controller: カート追加モーダル用
 * - cart_confirm_controller: カート確認ドロワー用（このファイル）
 *
 * 必要なターゲット要素:
 * - value: 数量表示・入力フィールド
 * - minus/plus: 数量増減ボタン
 * - price: 価格表示エリア
 * - container: 商品行コンテナ
 * - dateLabel: 日付ラベル
 * - counter: 数量カウンター表示
 * - totalPrice: 合計価格表示
 * - dayTotal: 日別合計表示
 * - errorMessage: エラーメッセージ表示エリア
 */
export default class extends Controller {
  static targets = [
    "value",
    "minus",
    "plus",
    "price",
    "container",
    "dateLabel",
    "counter",
    "totalPrice",
    "dayTotal",
    "errorMessage",
  ];
  static values = {
    limit: Number,
    date: String,
    unitPrice: Number,
    quantity: Number, // 数量を管理するためのvalue
    taxPercentage: Number, // 税率を管理するためのvalue
  };

  /**
   * コントローラー初期化処理
   * - 数量コントロール機能の初期化（valueTargetがある場合）
   * - 注文確定ボタン機能の準備（valueTargetがない場合）
   */
  connect() {
    // valueTargetが存在する場合のみ数量コントロール機能を初期化
    if (this.hasValueTarget) {
      // 初期数量を設定（valueTargetの値から取得）
      this.count = parseInt(this.valueTarget.value, 10) || 0;
      // 初期値を保存（ドロワーリセット用）
      this.originalCount = this.count;
      // 単価を取得
      this.unitPrice = this.unitPriceValue || 0;
      // バリデーション状態を初期化
      this.isValid = true;
      this.update();
    }
    // valueTargetが存在しない場合は注文確定ボタンとして機能（collectAndSubmitメソッドのみ使用）
  }

  /**
   * 数量を1増やす（+ボタン押下時）
   */
  increment() {
    if (this.count < this.limitValue) {
      this.count++;
      this.update();
    }
  }

  /**
   * 数量を1減らす（-ボタン押下時）
   */
  decrement() {
    if (this.count > 0) {
      this.count--;
      this.update();
    }
  }

  /**
   * UI要素の更新処理（数量変更後に実行）
   * - 入力フィールドの値更新
   * - ボタンの有効/無効切り替え
   * - 価格表示の更新
   * - スタイルの動的変更
   * - 合計金額の再計算
   */
  update() {
    // valueTargetが存在しない場合は何もしない（注文確定ボタンの場合）
    if (!this.hasValueTarget) return;

    // valueTargetの値を更新（inputフィールドの場合）
    this.valueTarget.value = this.count;
    this.plusTarget.disabled = this.count >= this.limitValue;
    this.minusTarget.disabled = this.count === 0;

    // quantityValueを更新（Stimulusのvalueとして管理）
    this.quantityValue = this.count;

    // 価格を更新
    if (this.hasPriceTarget) {
      const totalPrice = this.count * this.unitPrice;
      this.priceTarget.textContent = totalPrice.toLocaleString() + " 円";
    }

    // デザインを動的に変更
    this.updateStyles();

    // 合計金額を更新
    this.updateTotalPrice();
  }

  /**
   * 数量に応じた視覚的スタイルの動的変更
   * - 0個：グレー系
   * - 1個以上：緑系
   */
  updateStyles() {
    const hasItems = this.count > 0;

    // コンテナの背景色
    if (this.hasContainerTarget) {
      if (hasItems) {
        this.containerTarget.classList.add("bg-green-50");
      } else {
        this.containerTarget.classList.remove("bg-green-50");
      }
    }

    // 日付ラベルの色
    if (this.hasDateLabelTarget) {
      if (hasItems) {
        this.dateLabelTarget.classList.remove("text-foreground");
        this.dateLabelTarget.classList.add("text-green-700");
      } else {
        this.dateLabelTarget.classList.remove("text-green-700");
        this.dateLabelTarget.classList.add("text-foreground");
      }
    }

    // 価格の色
    if (this.hasPriceTarget) {
      if (hasItems) {
        this.priceTarget.classList.remove("text-foreground");
        this.priceTarget.classList.add("text-green-700");
      } else {
        this.priceTarget.classList.remove("text-green-700");
        this.priceTarget.classList.add("text-foreground");
      }
    }

    // カウンターの背景色と文字色
    if (this.hasCounterTarget) {
      if (hasItems) {
        this.counterTarget.classList.remove("bg-muted");
        this.counterTarget.classList.add("bg-green-100");
      } else {
        this.counterTarget.classList.remove("bg-green-100");
        this.counterTarget.classList.add("bg-muted");
      }
    }

    // 値の文字色
    if (this.hasValueTarget) {
      if (hasItems) {
        this.valueTarget.classList.remove("text-foreground");
        this.valueTarget.classList.add("text-emerald-700");
      } else {
        this.valueTarget.classList.remove("text-emerald-700");
        this.valueTarget.classList.add("text-foreground");
      }
    }

    // ボタンの枠線色
    [this.minusTarget, this.plusTarget].forEach((button) => {
      if (hasItems) {
        button.classList.remove("outline-border");
        button.classList.add("outline-green-700");
      } else {
        button.classList.remove("outline-green-700");
        button.classList.add("outline-border");
      }
    });
  }

  /**
   * 合計価格の更新処理
   */
  updateTotalPrice() {
    // カート確認ドロワーの日ごと合計と全体合計更新
    this.updateCartDrawerTotals();
  }

  /**
   * カート確認ドロワー内の合計金額・税額をリアルタイム更新
   * - 日別合計の計算・表示
   * - 全体合計の計算・表示
   * - 税額の計算・表示
   */
  updateCartDrawerTotals() {
    const drawer = document.querySelector("#cart-summary #confirm-cart-modal");
    if (!drawer) return;

    // 現在の日付を取得
    const currentDate = this.dateValue;
    if (!currentDate) return;

    // 日ごとの合計を更新
    const dayTotalElement = drawer.querySelector(
      `[data-cart-target="dayTotal"][data-date="${currentDate}"]`
    );
    if (dayTotalElement) {
      // その日の全商品の合計を計算（存在する要素のみ）
      const dayCartControllers = drawer.querySelectorAll(
        `[data-cart-date-value="${currentDate}"] [data-controller="cart-confirm"]`
      );
      let dayTotal = 0;

      dayCartControllers.forEach((controllerElement) => {
        // 要素がDOM内に存在するかチェック
        if (!document.contains(controllerElement)) return;

        const controller =
          this.application.getControllerForElementAndIdentifier(
            controllerElement,
            "cart-confirm"
          );
        if (controller) {
          const quantity = controller.count || 0;
          const unitPrice = controller.unitPrice || 0;
          dayTotal += quantity * unitPrice;
        }
      });

      dayTotalElement.textContent = dayTotal.toLocaleString() + " ";
    }

    // 全体の合計を更新（cart-summary内のtotalPrice）
    const cartSummary = document.querySelector(
      '#cart-summary [data-cart-target="totalPrice"]'
    );
    if (cartSummary) {
      const allCartControllers = drawer.querySelectorAll(
        '[data-controller="cart-confirm"]'
      );
      let grandTotal = 0;

      allCartControllers.forEach((controllerElement) => {
        // 要素がDOM内に存在するかチェック
        if (!document.contains(controllerElement)) return;

        const controller =
          this.application.getControllerForElementAndIdentifier(
            controllerElement,
            "cart-confirm"
          );
        if (controller) {
          const quantity = controller.count || 0;
          const unitPrice = controller.unitPrice || 0;
          grandTotal += quantity * unitPrice;
        }
      });

      cartSummary.innerHTML = `<span class="text-foreground text-lg font-bold leading-7">${grandTotal.toLocaleString()} </span><span class="text-foreground text-base font-medium leading-normal">円</span>`;

      // 税額表示も更新（各商品のtax_percentageを使った計算）
      const taxElement =
        cartSummary.parentElement.querySelector(".whitespace-nowrap");
      if (taxElement) {
        let totalTaxAmount = 0;

        allCartControllers.forEach((controllerElement) => {
          if (!document.contains(controllerElement)) return;

          const controller =
            this.application.getControllerForElementAndIdentifier(
              controllerElement,
              "cart-confirm"
            );
          if (controller) {
            const quantity = controller.count || 0;
            const unitPrice = controller.unitPrice || 0;
            const taxPercentage = controller.taxPercentageValue || 1.1;
            const taxAmount = Math.round(
              unitPrice * quantity * (taxPercentage - 1)
            );
            totalTaxAmount += taxAmount;
          }
        });

        taxElement.textContent = `(税額+${totalTaxAmount.toLocaleString()}円)`;
      }
    }
  }

  /**
   * 数量直接入力時のバリデーション処理
   * - 制限値チェック
   * - エラーメッセージ表示制御
   */
  validateInput() {
    if (!this.hasValueTarget) return;

    const inputValue = parseInt(this.valueTarget.value, 10) || 0;
    this.count = inputValue;

    // バリデーション
    if (inputValue > this.limitValue) {
      this.showError();
      this.isValid = false;
    } else {
      this.hideError();
      this.isValid = true;
    }

    this.update();
  }

  /**
   * エラーメッセージの表示
   */
  showError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = `${this.limitValue}個まで注文いただけます`;
      this.errorMessageTarget.classList.remove("hidden");
    } else {
      console.log("errorMessageTarget not found");
    }
  }

  /**
   * エラーメッセージの非表示
   */
  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden");
    }
  }

  /**
   * 日付のフォーマット（MM月DD日形式）
   * @return {string} フォーマット済み日付文字列
   */
  formatDate() {
    const date = new Date(this.dateValue);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}月${day}日`;
  }

  /**
   * カート確認ドロワーを閉じる（アニメーション付き）
   * - オーバーレイの削除
   * - z-indexの調整
   * - ボタン表示の切り替え
   * - 背景スクロールの復元
   */
  hideDrawer() {
    const confirmTarget = document.querySelector(
      '[data-products-target="confirm"]'
    );
    if (!confirmTarget) return;

    // オーバーレイを削除
    this.removeOverlay();

    // カートサマリーのz-indexを閉じた状態に変更
    const cartSummary = document.getElementById("cart-summary");
    if (cartSummary) {
      cartSummary.classList.remove("z-13");
      cartSummary.classList.add("z-10");
    }

    confirmTarget.classList.remove("h-[70vh]");
    confirmTarget.classList.add("h-0");
    confirmTarget.classList.remove("is-drawer-open");

    // ボタン表示を更新
    this.updateCartSummaryButtons(false);

    // 背景スクロールを復元
    document.body.style.overflow = "";

    setTimeout(() => {
      confirmTarget.classList.remove("flex");
    }, 300);
  }

  /**
   * カートサマリーのボタン表示状態を更新
   * @param {boolean} isDrawerOpen ドロワーが開いているかどうか
   */
  updateCartSummaryButtons(isDrawerOpen) {
    const cartButton = document.querySelector(
      '[data-products-target="cartButton"]'
    );
    const actionButtons = document.querySelector(
      '[data-products-target="actionButtons"]'
    );

    if (cartButton && actionButtons) {
      cartButton.hidden = isDrawerOpen;
      actionButtons.hidden = !isDrawerOpen;
    }
  }

  /**
   * ドロワー用オーバーレイの削除（フェードアウト付き）
   */
  removeOverlay() {
    // DOMから直接オーバーレイを取得して削除
    const overlay = document.querySelector(".fixed.inset-0.bg-black\\/20.z-12");
    if (!overlay) return;

    overlay.style.opacity = "0";
    setTimeout(() => {
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    }, 300);
  }

  /**
   * 戻る・注文確定ボタン押下時の処理
   * - 全商品のカートデータを収集
   * - 隠しフィールドとしてフォームに追加
   * - フォーム送信の実行
   * - 送信後の適切な画面制御
   * @param {Event} event ボタン押下イベント
   */
  collectAndSubmit(event) {
    // デフォルトの送信を一時停止
    event.preventDefault();

    // button_toが生成したフォームを取得
    const form = event.target.closest("form");
    if (!form) return;

    // 既存の隠しフィールドを削除
    form
      .querySelectorAll('input[name^="cart_items"]')
      .forEach((input) => input.remove());

    // 全てのcart-confirmコントローラーからデータを収集
    const cartControllers = document.querySelectorAll(
      '[data-controller*="cart-confirm"]'
    );

    cartControllers.forEach((element) => {
      const controller = this.application.getControllerForElementAndIdentifier(
        element,
        "cart-confirm"
      );
      if (controller && controller.count >= 0) {
        // 日付の隠しフィールド
        const dateInput = document.createElement("input");
        dateInput.type = "hidden";
        dateInput.name = "cart_items[][date]";
        dateInput.value = controller.dateValue;
        form.appendChild(dateInput);

        // 商品IDの隠しフィールド
        const productIdHidden = element.querySelector(
          'input[name*="product_id"]'
        );
        if (productIdHidden) {
          const productIdInput = document.createElement("input");
          productIdInput.type = "hidden";
          productIdInput.name = "cart_items[][product_id]";
          productIdInput.value = productIdHidden.value;
          form.appendChild(productIdInput);
        }

        // 数量の隠しフィールド
        const quantityInput = document.createElement("input");
        quantityInput.type = "hidden";
        quantityInput.name = "cart_items[][quantity]";
        quantityInput.value = controller.count;
        form.appendChild(quantityInput);
      }
    });

    // 送信前の処理を分岐
    const isBackButton = form.action.includes("apply_cart_changes");

    if (isBackButton) {
      // 戻るボタン: 即座にドロワーを閉じる
      this.hideDrawer();
    }

    // フォーム送信
    form.requestSubmit();

    if (!isBackButton) {
      // 注文確定: エラーの有無でドロワーを閉じるかを判定
      setTimeout(() => {
        const hasError = document.querySelector(".order-error");
        if (!hasError) {
          this.hideDrawer();
        }
      }, 100); // Turbo Stream更新を待つための短い遅延
    }
  }
}
